import * as React from 'react';
import { ArticleFragmentContext } from '../contexts';
import { IArticleFragment } from '../types';
import useArticles, { ArticlesReturnData } from '../hooks/useArticles';

type GroupFunction = (data: ArticlesReturnData) => React.ReactNode;

const isGroupFunction = (arg: any): arg is GroupFunction => {
  return typeof arg === 'function';
};

function returnChild(
  children: GroupFunction | React.ReactNode,
  data: ArticlesReturnData
) {
  return (
    <>
      {React.isValidElement(children)
        ? children
        : isGroupFunction(children)
        ? children(data)
        : null}
    </>
  );
}

type GroupProps = {
  categoryId?: string;
  children: GroupFunction | React.ReactNode;
};

/**
 * Articles just provides a way to access the categories data via a render prop.
 * You can pass in size and categoryId.
 * If you don't pass categoryId it will be fetched from the current category.
 * If you don't pass size then if have any already we just return what we have
 * else if we don't have articles yet for that category we fetch the default amount (4)
 */
export const Group: React.FC<GroupProps> = ({ children, categoryId }) => {
  const articlesData = useArticles(categoryId);
  return <>{returnChild(children, articlesData)}</>;
};

type ArticleLoopData = {
  index: number;
  article: IArticleFragment;
};
type ArticleLoopFunction = (data: ArticleLoopData) => React.ReactNode;
const isArticleLoopFunction = (arg: any): arg is ArticleLoopFunction => {
  return typeof arg === 'function';
};

/** Checks the type of the react child and returns in the correct way */
export function returnLoopChild(
  children: ArticleLoopFunction | React.ReactNode,
  data: ArticleLoopData
) {
  return React.isValidElement(children)
    ? children
    : isArticleLoopFunction(children)
    ? children(data)
    : null;
}

type LoopArticlesProps = {
  children: React.ReactNode | GroupFunction;
};
/**
 * For each of the articles of the either the current category in context or the categoryId passed in
 * it repeats the children passed in.
 * So if you pass a render prop as a child it will be called with each sub category result.
 * Else if you pass in React Elements they will be repeated for each sub category result.
 */
export const Loop = ({ children }: LoopArticlesProps) => {
  const { articles } = useArticles();
  if (!articles) return null;

  return (
    <>
      {articles.map((article, index) => (
        <ArticleFragmentContext.Provider key={article.id} value={article}>
          {returnLoopChild(children, {
            article,
            index,
          })}
        </ArticleFragmentContext.Provider>
      ))}
    </>
  );
};
